import React, { useContext, useEffect } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { ReferralType, BiEventType } from '@wix/da-bi/pkg/constants';
import {
  BiData,
  DefaultBiEvent,
  SubmitButtonClickBiEvent,
} from '@wix/da-bi/pkg/events';
import CoreBadge from '@wix/da-ds/pkg/Badges/CoreBadge';
import { URLS } from '@wix/da-url';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import BuyCoreButton from '@wix/da-shared-react/pkg/Button/BuyCoreButton';
import SophisticatedTooltipLink from '@wix/da-ds/pkg/tooltips/SophisticatedTooltipLink';
import SophisticatedTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SophisticatedTooltip';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { PapiUser } from '@wix/da-papi-types';

import EmptyState from '../../../../EmptyState';
import CoreExclusive from '../CoreExclusive';

import s from './PremiumFoldersEmptyState.scss';

export interface Props {
  view: 'wide' | 'large' | 'small';
  user: PapiUser;
  isOwner: boolean;
  onOpenChat: (user: PapiUser) => void;
  onCreateGallery: () => void;
  hasCoreMembership?: boolean;
}

export const PremiumFoldersEmptyState: React.FC<Props> = ({
  view,
  user,
  user: { username },
  isOwner,
  onOpenChat,
  onCreateGallery,
  hasCoreMembership,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isPaywallUpdates = useFlag('paywall_updates');

  useEffect(() => {
    if (isOwner && window.location.search.includes('new_premium_folder')) {
      window.history.replaceState(
        null,
        document.title,
        window.location.pathname
      );
      onCreateGallery();
    }
  }, [isOwner, onCreateGallery]);

  if (isOwner) {
    if (hasCoreMembership) {
      return isPaywallUpdates ? (
        <EmptyState
          className={classnames(s['root'], s['paywall_updates'], s[view])}
          title={t('pages.shop.emptyState.core.premiumFolder.title')}
          titleClassName={s['title']}
          subtitle={
            isMobile
              ? t('pages.shop.emptyState.core.premiumFolder.subtitle.mobile')
              : t('pages.shop.emptyState.core.premiumFolder.subtitle')
          }
          subtitleClassName={s['subtitle']}
          illustration={<CoreExclusive />}
          illustrationClassName={s['exclusive']}
          withButton={!isMobile}
          buttonLabel={t('pages.shop.emptyState.core.premiumFolder.cta')}
          onButtonClick={onCreateGallery}
          biData={BiData<DefaultBiEvent>({
            evid: 2,
            click_info: 'create_premium_gallery',
            sectionname: 'premium_gallery',
          })}
          view={'small'}
        />
      ) : (
        <EmptyState
          className={classnames(s['root'])}
          title={
            <>
              <SophisticatedTooltip
                size="medium"
                aria-label={t(
                  'pages.shop.emptyState.nonCore.premiumFolder.tooltip'
                )}
                description={t(
                  'pages.shop.emptyState.nonCore.premiumFolder.tooltip'
                )}
                postfix={
                  <SophisticatedTooltipLink href="https://www.deviantartsupport.com/en/article/what-is-a-core-membership">
                    {t('common.learnMore')}
                  </SophisticatedTooltipLink>
                }
              >
                <CoreBadge size="large" />
              </SophisticatedTooltip>{' '}
              {t('pages.shop.emptyState.core.premiumFolder.title')}
            </>
          }
          subtitle={t('pages.shop.emptyState.core.premiumFolder.subtitle')}
          subtitleClassName={s['subtitle']}
          withButton
          buttonLabel={t('common.submit')}
          buttonVariant={'approval-green'}
          onButtonClick={() => window.location.replace(URLS.SUBMIT)}
          biData={BiData<SubmitButtonClickBiEvent>({
            evid: 704,
            sectionname: 'premiumFolders',
          })}
        />
      );
    } else {
      // paywall_updates: clean up translation key as well
      return isPaywallUpdates ? (
        <EmptyState
          className={classnames(s['root'], s['paywall_updates'], s[view])}
          title={t('pages.shop.emptyState.nonCore.premiumFolder.title')}
          titleClassName={s['title']}
          subtitle={t(
            isMobile
              ? 'pages.shop.emptyState.nonCore.premiumFolder.subtitle__paywall_updates.mobile'
              : 'pages.shop.emptyState.nonCore.premiumFolder.subtitle__paywall_updates'
          )}
          subtitleClassName={s['subtitle__paywall_updates']}
          illustration={<CoreExclusive />}
          illustrationClassName={s['exclusive']}
          customButton={
            isMobile ? undefined : (
              <BuyCoreButton
                size="large"
                biData={{
                  referral_info: ReferralType.SHOP_PREMIUM_GALLERIES,
                }}
              >
                {t('widgets.tierContent.empty.cta.getCore')}
              </BuyCoreButton>
            )
          }
          view={'small'}
        />
      ) : (
        <EmptyState
          className={classnames(s['root'])}
          title={t('pages.shop.emptyState.nonCore.premiumFolder.title')}
          subtitle={
            <Trans i18nKey="pages.shop.emptyState.nonCore.premiumFolder.subtitle">
              <BiLink
                href={URLS.CORE_MEMBERSHIP}
                className={s['upgrade-to-core']}
                biData={{
                  evid: BiEventType.BUY_CORE_BUTTON,
                  referral_info: ReferralType.PAYWALL_PG,
                }}
              >
                Upgrade to Core{' '}
              </BiLink>
              <SophisticatedTooltip
                size="medium"
                className={s['core-icon']}
                aria-label={t(
                  'pages.shop.emptyState.nonCore.premiumFolder.tooltip'
                )}
                description={t(
                  'pages.shop.emptyState.nonCore.premiumFolder.tooltip'
                )}
                postfix={
                  <SophisticatedTooltipLink href="https://www.deviantartsupport.com/en/article/what-is-a-core-membership">
                    {t('common.learnMore')}
                  </SophisticatedTooltipLink>
                }
              >
                <CoreBadge size="large" />
              </SophisticatedTooltip>
              to let deviants pay you to unlock your exclusive content.
              <br />
              Add tutorials, works in progress, resources, and more.
            </Trans>
          }
          subtitleClassName={s['subtitle']}
        />
      );
    }
  }

  const handleOpenChat = () => onOpenChat(user);

  return (
    <EmptyState
      className={classnames(s['root'], s[view])}
      title={t('pages.shop.emptyState.premiumFolders.title.visitor', {
        username,
      })}
      subtitle={t('pages.shop.emptyState.premiumFolders.subtitle.visitor')}
      subtitleClassName={s['subtitle']}
      onButtonClick={handleOpenChat}
      buttonLabel={t('pages.shop.emptyState.openChatWithArtist')}
      withButton
    />
  );
};
PremiumFoldersEmptyState.displayName = 'PremiumFoldersEmptyState';

export default PremiumFoldersEmptyState;
